import axios from 'axios'

export interface CartItem {
  id: string
  name: string
  price: number
  quantity: number
  type: 'product' | 'offer'
  productId?: string
  offerId?: string
  reservationId: string
  merchantName?: string
  image?: string
  currencyCode?: string
}

export interface CartSession {
  sessionId: string
  userId?: string
  userEmail?: string
  items: CartItem[]
  totalItems: number
  totalValue: number
  lastActivity: string
  expiresAt: string
  status: 'active' | 'expired' | 'converted'
  createdAt: string
  merchantName?: string
}

export interface CartStatistics {
  activeSessions: number
  totalItems: number
  totalValue: number
  expiringSoon: number
  averageCartValue: number
  conversionRate: number
}

export interface CartFilters {
  status?: 'active' | 'expired' | 'converted'
  minValue?: number
  maxValue?: number
  search?: string
}

export interface PaginatedResponse<T> {
  data: T[]
  total: number
  success: boolean
}

// Use proxy configuration from vite.config.ts
const API_BASE_URL = '/api'

const cartApi = axios.create({
  baseURL: `${API_BASE_URL}/reservations`,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Add request interceptor for authentication
cartApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Add response interceptor for error handling
cartApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Handle unauthorized access
      localStorage.removeItem('access_token')
      window.location.href = '/login'
    }
    return Promise.reject(error)
  }
)

// Helper function to convert reservation data to cart session
const reservationToCartItem = (reservation: any): CartItem => {
  const isOffer = reservation.item_type === 'offer'
  
  return {
    id: isOffer ? reservation.offer_id : reservation.product_id,
    name: reservation.item_name || 'Article réservé',
    price: reservation.item_price || 0,
    quantity: reservation.quantity || 1,
    type: isOffer ? 'offer' : 'product',
    productId: isOffer ? undefined : reservation.product_id,
    offerId: isOffer ? reservation.offer_id : undefined,
    reservationId: reservation.reservation_id,
    merchantName: reservation.merchant_name,
    currencyCode: reservation.currency_code || 'MAD',
  }
}

// Helper function to aggregate reservations by session
const aggregateReservationsBySession = (reservations: any[]): CartSession[] => {
  const sessionsMap = new Map<string, CartSession>()
  
  reservations.forEach(reservation => {
    const sessionId = reservation.session_id
    const cartItem = reservationToCartItem(reservation)
    
    if (!sessionsMap.has(sessionId)) {
      sessionsMap.set(sessionId, {
        sessionId,
        userId: reservation.user_id,
        userEmail: reservation.user_email,
        items: [cartItem],
        totalItems: reservation.quantity,
        totalValue: (reservation.item_price || 0) * (reservation.quantity || 1),
        lastActivity: reservation.updated_at || reservation.created_at,
        expiresAt: reservation.expires_at,
        status: reservation.expires_at && new Date(reservation.expires_at) < new Date() ? 'expired' : 'active',
        createdAt: reservation.created_at,
        merchantName: reservation.merchant_name,
      })
    } else {
      const session = sessionsMap.get(sessionId)!
      session.items.push(cartItem)
      session.totalItems += reservation.quantity
      session.totalValue += (reservation.item_price || 0) * (reservation.quantity || 1)
      
      // Update last activity if this reservation is more recent
      const reservationDate = new Date(reservation.updated_at || reservation.created_at)
      const sessionDate = new Date(session.lastActivity)
      if (reservationDate > sessionDate) {
        session.lastActivity = reservation.updated_at || reservation.created_at
      }
    }
  })
  
  return Array.from(sessionsMap.values())
}

export const cartService = {
  // Get all cart sessions with optional filters
  async getAll(filters: CartFilters = {}): Promise<PaginatedResponse<CartSession>> {
    try {
      // Try to get data from API
      const response = await cartApi.get('/temporary/reservations/all')
      
      if (response.data.success && response.data.reservations) {
        const sessions = aggregateReservationsBySession(response.data.reservations)
        
        // Apply filters
        let filteredSessions = sessions
        
        if (filters.status) {
          filteredSessions = filteredSessions.filter(session => session.status === filters.status)
        }
        
        if (filters.minValue !== undefined) {
          filteredSessions = filteredSessions.filter(session => session.totalValue >= filters.minValue!)
        }
        
        if (filters.maxValue !== undefined) {
          filteredSessions = filteredSessions.filter(session => session.totalValue <= filters.maxValue!)
        }
        
        if (filters.search) {
          const searchLower = filters.search.toLowerCase()
          filteredSessions = filteredSessions.filter(session =>
            session.sessionId.toLowerCase().includes(searchLower) ||
            (session.userEmail && session.userEmail.toLowerCase().includes(searchLower)) ||
            (session.merchantName && session.merchantName.toLowerCase().includes(searchLower))
          )
        }
        
        return {
          data: filteredSessions,
          total: filteredSessions.length,
          success: true
        }
      }
      
      return {
        data: [],
        total: 0,
        success: false
      }
    } catch (error) {
      console.error('Error fetching cart sessions from API:', error)
      return {
        data: [],
        total: 0,
        success: false
      }
    }
  },

  // Get cart session by ID
  async getById(sessionId: string): Promise<{ success: boolean; data?: CartSession; error?: string }> {
    try {
      const response = await cartApi.get(`/temporary/sessions/${sessionId}/reservations`)
      
      if (response.data.success && response.data.reservations) {
        const sessions = aggregateReservationsBySession(response.data.reservations)
        const session = sessions.find(s => s.sessionId === sessionId)
        
        if (session) {
          return {
            success: true,
            data: session
          }
        }
      }
      
      return {
        success: false,
        error: 'Session not found'
      }
    } catch (error) {
      console.error('Error fetching cart session:', error)
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      }
    }
  },

  // Get cart statistics
  async getStatistics(): Promise<{ success: boolean; data?: CartStatistics; error?: string }> {
    try {
      const response = await this.getAll()
      
      if (!response.success) {
        return {
          success: false,
          error: 'Failed to fetch cart data'
        }
      }
      
      const sessions = response.data
      const activeSessions = sessions.filter(s => s.status === 'active')
      const now = new Date()
      const fiveMinutesFromNow = new Date(now.getTime() + 5 * 60 * 1000)
      
      const expiringSoon = activeSessions.filter(s => {
        const expiresAt = new Date(s.expiresAt)
        return expiresAt < fiveMinutesFromNow && expiresAt > now
      }).length
      
      const totalItems = activeSessions.reduce((sum, session) => sum + session.totalItems, 0)
      const totalValue = activeSessions.reduce((sum, session) => sum + session.totalValue, 0)
      const averageCartValue = activeSessions.length > 0 ? totalValue / activeSessions.length : 0
      
      // For now, we'll set conversion rate to 0 as we don't have historical data
      // In a real implementation, we would track conversions over time
      const conversionRate = 0
      
      return {
        success: true,
        data: {
          activeSessions: activeSessions.length,
          totalItems,
          totalValue,
          expiringSoon,
          averageCartValue,
          conversionRate
        }
      }
    } catch (error) {
      console.error('Error calculating cart statistics:', error)
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      }
    }
  },

  // Force expire a cart session
  async forceExpire(sessionId: string): Promise<{ success: boolean; message?: string; error?: string }> {
    try {
      // First, get all reservations for this session
      const sessionResponse = await this.getById(sessionId)
      
      if (!sessionResponse.success || !sessionResponse.data) {
        return {
          success: false,
          error: 'Session not found'
        }
      }
      
      // Delete each reservation in the session
      const deletePromises = sessionResponse.data.items.map(item =>
        cartApi.delete(`/temporary/reservations/${item.reservationId}`)
      )
      
      await Promise.all(deletePromises)
      
      return {
        success: true,
        message: 'Session expired successfully'
      }
    } catch (error) {
      console.error('Error forcing session expiration:', error)
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      }
    }
  },

  // Get all temporary reservations (raw data)
  async getAllReservations(): Promise<{ success: boolean; data?: any[]; error?: string }> {
    try {
      const response = await cartApi.get('/temporary/reservations/all')
      
      if (response.data.success) {
        return {
          success: true,
          data: response.data.reservations || []
        }
      }
      
      return {
        success: false,
        error: 'Failed to fetch reservations'
      }
    } catch (error) {
      console.error('Error fetching reservations:', error)
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      }
    }
  }
}

export default cartService